/**
 * useScreenTimeRules.js - React hook for screen time rules
 *
 * Listens to Firestore rules in real-time and provides:
 * - activeRules: currently active screen time rules
 * - isCurrentlyLocked: whether device should be/is locked
 * - nextUnlockTime: when the next unlock will happen
 * - allRules: all rules (active and scheduled)
 */

import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Hook for child-facing screen time state
 * @param {string} familyId
 * @param {string} childId
 */
export function useScreenTimeRules(familyId, childId) {
  const [allRules, setAllRules] = useState([]);
  const [activeRules, setActiveRules] = useState([]);
  const [isCurrentlyLocked, setIsCurrentlyLocked] = useState(false);
  const [nextUnlockTime, setNextUnlockTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const evaluateRules = useCallback((rules) => {
    const now = new Date();
    const active = [];
    let locked = false;
    let earliest = null;

    for (const rule of rules) {
      if (!rule.screenTimeEnabled) continue;

      const startTime = rule.startTime?.toDate?.() || (rule.startTime ? new Date(rule.startTime) : null);
      const endTime = rule.endTime?.toDate?.() || (rule.endTime ? new Date(rule.endTime) : null);

      let isActive = false;

      if (rule.recurring && rule.recurringDays?.length > 0) {
        const todayName = DAY_NAMES[now.getDay()];
        if (rule.recurringDays.includes(todayName) && startTime && endTime) {
          const todayStart = todayAt(startTime);
          const todayEnd = todayAt(endTime);
          isActive = now >= todayStart && now <= todayEnd;
        }
      } else if (startTime && endTime) {
        isActive = now >= startTime && now <= endTime;
        if (isActive && endTime && endTime > now) {
          if (!earliest || endTime < earliest) {
            earliest = endTime;
          }
        }
      } else if (startTime && !endTime) {
        isActive = now >= startTime;
      }

      if (isActive) {
        active.push(rule);
        locked = true;
      }
    }

    setActiveRules(active);
    setIsCurrentlyLocked(locked);
    setNextUnlockTime(earliest);
  }, []);

  useEffect(() => {
    if (!familyId || !childId) {
      setLoading(false);
      return;
    }

    const rulesRef = collection(db, `families/${familyId}/screenTimeRules`);
    const q = query(
      rulesRef,
      where('childId', '==', childId),
      where('screenTimeEnabled', '==', true),
      orderBy('startTime', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const rules = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setAllRules(rules);
        evaluateRules(rules);
        setLoading(false);
      },
      (err) => {
        console.error('[useScreenTimeRules] Error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    // Re-evaluate every 60 seconds in case rules cross time boundaries
    const timer = setInterval(() => {
      evaluateRules(allRules);
    }, 60 * 1000);

    return () => {
      unsubscribe();
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, childId, evaluateRules]);

  return {
    allRules,
    activeRules,
    isCurrentlyLocked,
    nextUnlockTime,
    loading,
    error,
  };
}

/**
 * Hook for parent-facing screen time rules (all children)
 * @param {string} familyId
 */
export function useAllScreenTimeRules(familyId) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!familyId) {
      setLoading(false);
      return;
    }

    const rulesRef = collection(db, `families/${familyId}/screenTimeRules`);
    const q = query(rulesRef, orderBy('startTime', 'asc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const ruleData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setRules(ruleData);
        setLoading(false);
      },
      (err) => {
        console.error('[useAllScreenTimeRules] Error:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [familyId]);

  return { rules, loading, error };
}

/**
 * Helper: return a Date representing "today at the same time as the given date"
 */
function todayAt(date) {
  const today = new Date();
  today.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
  return today;
}

export default useScreenTimeRules;
